// ─────────────────────────────────────────────
// DirayahAI Widget — All TypeScript Interfaces
// Single source of truth for all types used
// across components, hooks, services and store.
// ─────────────────────────────────────────────

// ── Widget configuration ──────────────────────
// Populated by useWidgetConfig from URL params
// injected by loader.js into the iFrame src.
export interface WidgetConfig {
  tenantSlug: string       // data-tenant on script tag
  domainId: string         // data-domain on script tag
  apiUrl: string           // data-api-url or default proxy
  theme: 'light' | 'dark'  // data-theme on script tag
  defaultLanguage: string  // data-lang on script tag (e.g. 'English')
  title: string            // data-title — shown in chat header
  logoUrl?: string         // data-logo — optional tenant logo
}

// ── Chat message ──────────────────────────────
export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  isStreaming?: boolean     // True while SignalR tokens are arriving
  sources?: RetrievedChunk[] // Document chunks returned by RAG
  language?: string
  isRestriction?: boolean 
}

// ── RAG source chunk ──────────────────────────
// Returned by /api/chat/ask in retrievedChunks[]
export interface RetrievedChunk {
  chunkId: string
  documentTitle: string
  excerpt: string
  score: number
}

// ── Chat session ──────────────────────────────
export interface ChatSession {
  id: string
  title: string
  domainId: string
  createdAt: Date
  totalTokensUsed: number
}

// ── API request/response shapes ───────────────
// Must match DirayahAI.Application DTOs exactly
export interface AskQuestionRequest {
  tenantId: string
  domainId: string
  userId?: string
  sessionId?: string
  message: string
  topK?: number
  minScore?: number
  maxHistoryMessages?: number
  responseLanguage?: string
}

export interface AskQuestionResponse {
  sessionId: string
  messageId: string
  answer: string
  retrievedChunks: RetrievedChunk[]
  llmProvider: string
  llmModel: string
  latencyMs: number
  totalTokens: number
  warningMessage?: string  // Set when trial expired, falling back to Ollama
}

export interface SessionResponse {
  id: string
  title: string
  domainId: string
  createdAt: string
  totalTokensUsed: number
}

export interface MessageResponse {
  id: string
  role: number             // 0=System, 1=User, 2=Assistant (matches MessageRole enum)
  content: string
  llmProvider?: string
  llmModel?: string
  latencyMs?: number
  createdAt: string
}

export interface WidgetConfig {
  tenantSlug: string
  domainId: string
  tenantId: string
  widgetApiKey: string      // ← NEW — passed via data-api-key on script tag
  apiUrl: string
  theme: 'light' | 'dark'
  defaultLanguage: string
  title: string
  logoUrl?: string
}

// ── Appointment booking ───────────────────────

export interface AvailableSlot {
  date: string       // "yyyy-MM-dd"
  dayLabel: string   // "Mon 26 May"
  timeLabel: string  // "10:00 AM"
  slotStart: string  // "HH:mm"
  slotEnd: string    // "HH:mm"
}

export type BookingStep = null | 'name' | 'whatsapp' | 'email' | 'slot'

export interface BookingData {
  name: string
  whatsapp: string
  email: string
}

// ── Supported languages ───────────────────────
// Shown in the LanguageSelector dropdown
export const SUPPORTED_LANGUAGES = [
  { code: 'English',    label: 'English',    flag: '🇬🇧' },
  { code: 'Arabic',     label: 'العربية',    flag: '🇦🇪' },
  { code: 'French',     label: 'Français',   flag: '🇫🇷' },
  { code: 'Urdu',       label: 'اردو',       flag: '🇵🇰' },
  { code: 'Hindi',      label: 'हिन्दी',     flag: '🇮🇳' },
  { code: 'Malayalam',  label: 'മലയാളം',     flag: '🇮🇳' },
  { code: 'Filipino',   label: 'Filipino',   flag: '🇵🇭' },
  { code: 'German',     label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'Italian',    label: 'Italiano',   flag: '🇮🇹' },
  { code: 'Turkish',    label: 'Türkçe',     flag: '🇹🇷' },
] as const

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]['code']

